// Dashboards manager per mockups/07-dashboards.html: live rows with status
// tag + phase pill + updated date, search, and the new-dashboard modal
// (folds the old /dashboards/new page in). "Archived" is skipped per Henry —
// the manifest has no archived flag yet. Hub data comes from the
// hsp-dashboards worker; locally the service binding is absent, so the page
// degrades to the setup note.

import { useViewState } from '../lib/useViewState';
import { oneOf, str } from '../lib/viewState';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import type { CommsSummary } from '../lib/dashboardComms';
import { CommsTabs, DashboardHubCards, DigestView, WaitingView, type HubCommsView } from '../components/dashboardComms/DashboardComms';
import { Modal, PageHeader, SelectField, SkeletonRows } from '../components/primitives';
import { DashboardsIcon, SearchIcon } from '../components/icons';
import { useIsMobile } from '../lib/useIsMobile';
import { useQuickAddAction } from '../lib/quickAdd';

export const DASHBOARD_BASE = 'https://dashboard.sheahanstudios.com';

export const DASHBOARD_PHASES = [
  { key: 'pre-production', label: 'Pre-Production' },
  { key: 'shoot', label: 'Shoot' },
  { key: 'post', label: 'Post-Production' },
  { key: 'delivered', label: 'Delivered' },
] as const;

export const dashboardsViewCodec = {
  query: str('q'),
  view: oneOf('view', ['dashboards', 'digest', 'waiting'] as const, 'dashboards'),
};

export default function Dashboards() {
  const { initial, publish } = useViewState(dashboardsViewCodec, state => {
    setQuery(state.query);
    setView(state.view);
  });
  const [summaries, setSummaries] = useState<CommsSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unconfigured, setUnconfigured] = useState(false);
  const [query, setQuery] = useState(() => initial.query);
  const [view,setView] = useState<HubCommsView>(()=>initial.view);

  useEffect(() => {
    publish({ query, view });
  }, [publish, query, view]);
  const [showNew, setShowNew] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // A quick-added dashboard (or any other mutation) refreshes the list in
  // place — coalesced at 250ms like every other list page, with a sequence
  // guard so a slow response can never overwrite a newer one.
  const seq = useRef(0);
  const mutatedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchSummaries = useCallback(() => {
    const mine = ++seq.current;
    api<{ summaries: CommsSummary[] }>('/api/admin/dashboards/hub-data')
      .then((r) => {
        if (mine !== seq.current) return;
        setSummaries(r.summaries);
        setError(null); setUnconfigured(false);
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        if (e instanceof ApiError && e.status === 503) setUnconfigured(true);
        else setError(e.message);
      });
  }, []);

  useEffect(() => {
    fetchSummaries();
    const onMutated = () => {
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      mutatedTimer.current = setTimeout(fetchSummaries, 250);
    };
    window.addEventListener('ss:mutated', onMutated);
    return () => {
      window.removeEventListener('ss:mutated', onMutated);
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
    };
  }, [fetchSummaries]);

  // The FAB's quick-add sheet offers "New dashboard" — but not while the
  // integration is missing, when creating one would 503.
  useQuickAddAction(
    unconfigured ? null : { label: 'New dashboard', icon: <DashboardsIcon />, onSelect: () => setShowNew(true) },
  );

  return (
    <>
      <PageHeader
        title={isMobile ? 'Dashboards' : 'Client dashboards'}
        count={summaries ? `${summaries.length} live` : undefined}
        liveLabel={unconfigured ? 'Not configured' : 'Integration OK'}
        mobileActions={
          isMobile
            ? [
                {
                  key: 'search',
                  label: 'Search',
                  icon: <SearchIcon />,
                  onPress: () => window.dispatchEvent(new Event('ss:open-search')),
                },
              ]
            : undefined
        }
      />

      <CommsTabs view={view} onChange={v=>setView(v as HubCommsView)} items={[{value:'dashboards',label:'Dashboards'},{value:'digest',label:'Digests'},{value:'waiting',label:'Waiting'}]}/>

      {/* The healthy state already reads "Integration OK · Fri 11 Sep" in the
          mobile header's sync strip, so only the alert gets its own red mono
          line (mockup §13.1 A). */}
      {isMobile ? (
        unconfigured && <div className="dash-m-state bad">● NOT CONFIGURED</div>
      ) : (
        <div className="toolbar">
          <button className="btn primary" onClick={() => setShowNew(true)} disabled={unconfigured}>
            + New dashboard
          </button>
        </div>
      )}

      <div className="page-body">
        <div
          className="cc-hub-host"
          style={{ paddingTop: 4 }}
        >
          {unconfigured && isMobile && (
            <div className="dash-m-unconfigured">
              <p className="dash-m-note">
                Dashboard integration not configured — set the shared secret, it takes effect
                immediately:
              </p>
              <pre className="dash-m-code">
                cd ss-custom-os &amp;&amp; npx wrangler secret put DASHBOARD_WRITE_SECRET
              </pre>
            </div>
          )}
          {unconfigured && !isMobile && (
            <div className="group" style={{ marginTop: 24 }}>
              <div className="empty-note">
                Dashboard integration not configured — set the shared secret, it takes effect
                immediately:
              </div>
              <pre
                style={{
                  marginTop: 10,
                  padding: '10px 14px',
                  background: 'var(--panel-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  color: 'var(--text-2)',
                }}
              >
                cd ss-custom-os && npx wrangler secret put DASHBOARD_WRITE_SECRET
              </pre>
            </div>
          )}
          {error && <p className="error-text" style={{ marginTop: 24 }}>{error}</p>}

          {/* Quiet loading state (Phase 1.3) — skeleton until the first fetch
              settles, instead of a blank page before the unconfigured/error/
              live states are known. */}
          {summaries === null && !unconfigured && !error && (
            <div className="group" style={{ marginTop: 24 }}>
              <SkeletonRows />
            </div>
          )}

          {summaries && (view === 'digest' ? <DigestView summaries={summaries}/> : view === 'waiting' ? <WaitingView summaries={summaries}/> : <DashboardHubCards summaries={summaries} query={query} onQuery={setQuery} onCompose={()=>setView('digest')}/>)}

        </div>
      </div>

      {showNew && (
        <NewDashboardModal
          onClose={() => setShowNew(false)}
          onCreated={(slug) => navigate(`/dashboards/${slug}`)}
        />
      )}
    </>
  );
}

// ── New dashboard modal (folds the legacy /dashboards/new page in) ────────

function suggestSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

export function ToggleChip({
  label,
  on,
  onToggle,
}: {
  label: string;
  on: boolean;
  onToggle: () => void;
}) {
  return (
    <button type="button" className={`toggle-chip${on ? ' on' : ''}`} onClick={onToggle}>
      {label}
    </button>
  );
}

function NewDashboardModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (slug: string) => void;
}) {
  const [form, setForm] = useState({
    client_name: '',
    slug: '',
    project_name: '',
    project_summary: '',
    contact_name: '',
    contact_email: '',
    proposal_url: '',
    package_name: '',
    package_price: '',
    package_deposit: '',
    current_phase: 'pre-production',
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [show, setShow] = useState({ production: true, review: true, commercial: true });
  const [linkClient, setLinkClient] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => {
      const next = { ...f, [key]: e.target.value };
      if (key === 'client_name' && !slugTouched) next.slug = suggestSlug(e.target.value);
      return next;
    });

  const submit = async () => {
    if (busy || !form.client_name.trim() || !form.slug.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api('/api/admin/dashboards/create', {
        method: 'POST',
        json: {
          ...form,
          show_production: show.production,
          show_review: show.review,
          show_commercial: show.commercial,
          link_client: linkClient,
        },
      });
      onCreated(form.slug);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed.');
      setBusy(false);
    }
  };

  return (
    <Modal
      title="New dashboard"
      context="DASHBOARDS"
      onClose={onClose}
      hint="Goes live at the public URL immediately"
      foot={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            onClick={submit}
            disabled={busy || !form.client_name.trim() || !form.slug.trim()}
          >
            Create dashboard →
          </button>
        </>
      }
    >
      <div className="form-grid dash-new-form">
        <div className="f-field">
          <span className="f-label">
            Client name <span className="req">*</span>
          </span>
          <input className="f-input" value={form.client_name} onChange={set('client_name')} autoFocus />
        </div>
        <div className="f-field">
          <span className="f-label">
            Slug (URL path) <span className="req">*</span>
          </span>
          <input
            className="f-input mono"
            value={form.slug}
            placeholder="acme-co"
            onChange={(e) => {
              setSlugTouched(true);
              setForm((f) => ({ ...f, slug: e.target.value }));
            }}
          />
        </div>
        <div className="f-field dash-pair">
          <span className="f-label">Project name</span>
          <input className="f-input" value={form.project_name} onChange={set('project_name')} />
        </div>
        <div className="f-field dash-pair">
          <span className="f-label">Starting phase</span>
          <SelectField
            items={DASHBOARD_PHASES.map((p) => ({ value: p.key, label: p.label }))}
            value={form.current_phase as (typeof DASHBOARD_PHASES)[number]['key']}
            onChange={(current_phase) => setForm((f) => ({ ...f, current_phase }))}
          />
        </div>
        <div className="f-field span-2">
          <span className="f-label">Project summary</span>
          <input
            className="f-input"
            value={form.project_summary}
            placeholder="One line the client sees under the project name"
            onChange={set('project_summary')}
          />
        </div>
        <div className="f-field dash-pair">
          <span className="f-label">Contact name</span>
          <input className="f-input" value={form.contact_name} onChange={set('contact_name')} />
        </div>
        <div className="f-field dash-pair">
          <span className="f-label">Contact email</span>
          <input className="f-input mono" value={form.contact_email} onChange={set('contact_email')} />
        </div>
        <div className="f-field span-2">
          <span className="f-label">Proposal URL</span>
          <input
            className="f-input mono"
            value={form.proposal_url}
            placeholder="https://proposals.…"
            onChange={set('proposal_url')}
          />
        </div>
        <div className="f-field dash-pair">
          <span className="f-label">Package name</span>
          <input className="f-input" value={form.package_name} onChange={set('package_name')} />
        </div>
        <div className="f-field dash-pair">
          <span className="f-label">Package price</span>
          <input
            className="f-input money"
            value={form.package_price}
            placeholder="e.g. $8,500 + GST"
            onChange={set('package_price')}
          />
        </div>
        <div className="f-field">
          <span className="f-label">Package deposit</span>
          <input
            className="f-input"
            value={form.package_deposit}
            placeholder="e.g. 50%"
            onChange={set('package_deposit')}
          />
        </div>
        <div className="f-field span-2">
          <span className="f-label">Sections shown</span>
          <div className="dash-sections-row" style={{ display: 'flex', gap: 6 }}>
            {(['production', 'review', 'commercial'] as const).map((key) => (
              <ToggleChip
                key={key}
                label={key}
                on={show[key]}
                onToggle={() => setShow((s) => ({ ...s, [key]: !s[key] }))}
              />
            ))}
            <span style={{ width: 1, background: 'var(--border)' }} />
            <ToggleChip label="Link client record" on={linkClient} onToggle={() => setLinkClient((v) => !v)} />
          </div>
        </div>
        {error && <p className="error-text span-2">{error}</p>}
      </div>
    </Modal>
  );
}
